import { useState, useEffect } from "react";
import {
  Dialog,
  Grid,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@material-ui/core";
import StructureFields from "components/_refactor/fields/structure";
import MonthYearField from "components/_refactor/fields/month-year";
import Api from "lib/api/httpRequestHelper";
import { Info } from "@material-ui/icons";
import TableGoalsBySeller, {
  calculateGoalsDistribution,
} from "./table-vendedores";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import getMonthByNumber from "utils/get-month-by-number";

function parsePeriodo(periodo) {
  if (!periodo) return { month: "", year: "" };

  const meses = {
    janeiro: 1,
    fevereiro: 2,
    março: 3,
    abril: 4,
    maio: 5,
    junho: 6,
    julho: 7,
    agosto: 8,
    setembro: 9,
    outubro: 10,
    novembro: 11,
    dezembro: 12,
  };

  const [nomeMes, ano] = periodo.toLowerCase().split("/");
  const mesNumero = meses[nomeMes?.trim()] || "";

  return {
    month: mesNumero,
    year: Number(ano?.trim()),
  };
}

const MONTH_INDEX = new Date().getMonth() + 1;
const YEAR = new Date().getFullYear();

export default function SellerDialog({ open, onClose, goal }) {
  const [structureFilters, setStructureFilters] = useState({});
  const [month, setMonth] = useState({
    value: MONTH_INDEX,
    label: getMonthByNumber(MONTH_INDEX),
  });
  const [year, setYear] = useState({ value: YEAR, label: String(YEAR) });
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [goalId, setGoalId] = useState("");
  const [bonusValue, setBonusValue] = useState(goal?.MetaLoja || "");
  const [sellers, setSellers] = useState(goal?.ListVendedor || []);

  const [searching, setSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const snackbar = useSnackbar();

  useEffect(() => {
    if (goal?.Periodo) {
      const { month: parsedMonth, year: parsedYear } = parsePeriodo(
        goal.Periodo
      );
      setMonth({
        value: parsedMonth,
        label: getMonthByNumber(parsedMonth),
      });
      setYear({
        value: parsedYear,
        label: String(parsedYear),
      });
    }
  }, [goal]);

  async function handleSearch() {
    setSearching(true);
    try {
      const response = await Api.get(
        "goal-store-employee",
        "goals",
        `?idGrupoEconomico=${structureFilters.idGrupoEconomico}&idLoja=${structureFilters.idLojaUnica}&mes=${month.value}&ano=${year.value}`,
        true
      );
      const storeGoal = response.Result.MetaLoja;
      setBonusValue(storeGoal);

      const fetchedSellers = response.Result.ListVendedor || [];
      const sellersWithCalculatedGoals = calculateGoalsDistribution(
        storeGoal,
        fetchedSellers,
        month.value,
        year.value
      );

      setSellers(sellersWithCalculatedGoals);
      setPage(response.Result.Pagina || 0);
      setTotalPages(response.Result.TotalDePaginas || 0);
      setGoalId(response.Result.IdPeriodoMeta || "");
    } catch (error) {
      snackbar.showMessage("Erro ao buscar a meta da loja: " + error.message, {
        severity: "error",
      });
    } finally {
      setSearching(false);
    }
  }

  const handleCreate = async () => {
    if (
      !goal &&
      (!structureFilters.idGrupoEconomico ||
        !structureFilters.idLojaUnica ||
        !month ||
        !year)
    ) {
      setErrorMessage("Preencha grupo econômico, loja e período (mês/ano).");
      return;
    }
    setErrorMessage("");
    setSearching(true);

    try {
      await Api.post(
        "goal-store-employee-creation",
        "goals",
        {
          ...(goal && { IdPeriodoMeta: goal.IdPeriodo }),
          idGrupoEconomico: goal
            ? goal.IdGrupoEconomico
            : structureFilters.idGrupoEconomico,
          idLoja: goal ? goal.IdLoja : structureFilters.idLojaUnica,
          mes: month.value,
          ano: year.value,
          ListVendedor: sellers.map((seller) => ({
            IdVendedor: seller.IdVendedor,
            Nome: seller.Nome,
            Status: seller.Status,
            DiasOff: seller.DiasOff,
            Meta: seller.Meta,
          })),
        },
        true
      );
      snackbar.showMessage("Metas criadas com sucesso", {
        severity: "success",
      });
      onClose();
    } catch (error) {
      snackbar.showMessage("Erro ao criar a metas: " + error.message, {
        severity: "error",
      });
    } finally {
      setSearching(false);
    }
  };

  const handlePageChange = (page) => {
    setPage(page - 1);
    handleSearch();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      maxWidth={"xl"}
      disableEnforceFocus
    >
      <DialogTitle>
        <Typography>
          {goal ? "Editar" : "Criar"} Metas por Vendedores
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          {goal ? (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Loja</Typography>
                <Typography variant="caption">{goal.Loja}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Período</Typography>
                <Typography variant="caption">{goal.Periodo}</Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={10}>
                <Grid container spacing={2}>
                  <StructureFields
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching || sellers.length > 0}
                    onStructureFieldsLoad={setStructureFilters}
                    requiredFields={["economicGroup", "storeSingle"]}
                    hideFields={["brand", "channel", "representative", "store"]}
                    showSingleStore
                  />
                  <MonthYearField
                    month={month}
                    handleMonthChange={setMonth}
                    year={year}
                    handleYearChange={setYear}
                    disabled={searching || sellers.length > 0}
                    required={true}
                    displayEmpty={false}
                    direction="future"
                  />
                </Grid>
              </Grid>
              <Grid item xs={2} justify="center">
                <Button
                  onClick={handleSearch}
                  variant="contained"
                  disabled={
                    searching ||
                    !structureFilters.idGrupoEconomico ||
                    !structureFilters.idLojaUnica ||
                    !month ||
                    !year ||
                    sellers.length > 0
                  }
                  style={{ width: "100%", margin: "21px 0px 42px" }}
                >
                  Buscar
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item xs={12} style={{ marginTop: "1rem" }}>
          <Divider />
        </Grid>

        <TableGoalsBySeller
          goal={bonusValue}
          data={sellers}
          setData={setSellers}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          month={month}
          year={year}
        />

        {errorMessage && (
          <Grid item xs={12}>
            <Typography
              variant="h5"
              component="h5"
              style={{
                color: "grey",
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <Info />
              {errorMessage}
            </Typography>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          style={{ width: "150px" }}
          disabled={searching || !sellers.length}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
